import { Navigate } from 'react-router-dom';

import Box from '@mui/material/Box';

import mainBackgroundImage from '@/assets/welcomeBackgroundImage.png';
import type { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';

import { Button } from '@/components/atoms/Button';
import { Popup } from '@/components/atoms/Popup';

import { LoginCard } from './components/LoginCard';
import { LoginFooter } from './components/LoginFooter';
import { LoginMarketingCopy } from './components/LoginMarketingCopy';
import { useLoginForm } from './hooks/useLoginForm';

function selectIsAuthed(state: RootState) {
  return Boolean(state.auth.token ?? state.auth.user?.token);
}

function LoginPageContent() {
  const {
    email,
    setEmail,
    otp,
    setOtp,
    otpSent,
    otpTimeout,
    otpResendCount,
    isSendingOtp,
    isVerifyingOtp,
    isGoogleSigningIn,
    sendOtp,
    resendOtp,
    verifyOtp,
    resetEmailState,
    signInWithGoogle,
    contactOpen,
    openContact,
    closeContact,
  } = useLoginForm();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${mainBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(43, 43, 43, 0.6)',
        }}
      />

      <LoginMarketingCopy />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 8, sm: 6 },
        }}
      >
        <LoginCard
          email={email}
          onEmailChange={setEmail}
          otp={otp}
          onOtpChange={setOtp}
          otpSent={otpSent}
          otpTimeout={otpTimeout}
          otpResendCount={otpResendCount}
          isSendingOtp={isSendingOtp}
          isVerifyingOtp={isVerifyingOtp}
          isGoogleSigningIn={isGoogleSigningIn}
          onSendOtp={sendOtp}
          onResendOtp={resendOtp}
          onVerifyOtp={verifyOtp}
          onChangeEmail={resetEmailState}
          onGoogleSignIn={signInWithGoogle}
          onContactAdmin={openContact}
        />
      </Box>

      <LoginFooter />

      <Popup
        open={contactOpen}
        onClose={closeContact}
        title="Contact your administrator"
        actions={
          <Button onClick={closeContact} sx={{ minWidth: 96 }}>
            Close
          </Button>
        }
      >
        If you need access to FF&E, reach out to your organization&apos;s administrator so they can
        invite you or restore your account.
      </Popup>
    </Box>
  );
}

export default function LoginPage() {
  const authed = useAppSelector(selectIsAuthed);

  if (authed) {
    return <Navigate to="/home" replace />;
  }

  return <LoginPageContent />;
}
